import { useEffect, useState, MouseEvent } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar, Avatar, Box, Button, Divider, Drawer, IconButton, ListItemIcon, ListItemText,
  Menu, MenuItem, Stack, Toolbar, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckIcon from '@mui/icons-material/Check';
import LogoutIcon from '@mui/icons-material/Logout';
import { useMsal } from '@azure/msal-react';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import { useCurrentUser } from '../../auth/useCurrentUser';
import { DEV_AUTH_BYPASS } from '../../auth/devAuth';
import { LANGUAGES, currentLanguage, setLanguage } from '../../i18n';
import { SearchDialog } from '../search/SearchDialog';

/** `testId` is fixed rather than derived from the label, so the Playwright selectors do not
 *  change when the UI language does. */
const NAV = [
  { to: '/', labelKey: 'nav.pipeline', testId: 'pipeline', end: true },
  { to: '/customers', labelKey: 'nav.customers', testId: 'customers', end: false },
  { to: '/partners', labelKey: 'nav.partners', testId: 'partners', end: false },
  { to: '/my-actions', labelKey: 'nav.myActions', testId: 'my-actions', end: false },
];

export function AppShell() {
  const theme = useTheme();
  const navigate = useNavigate();
  // Subscribing here is what re-renders the whole page tree on a language change, which is also
  // what refreshes the Intl-formatted numbers and dates further down.
  const { t } = useTranslation();
  const { mode, resolved, setMode } = useThemeMode();
  const user = useCurrentUser();
  const { instance } = useMsal();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [avatarAnchor, setAvatarAnchor] = useState<null | HTMLElement>(null);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  // App.tsx owns the Ctrl/⌘+K binding and announces it here, so the shortcut works everywhere.
  useEffect(() => {
    const open = () => setSearchOpen(true);
    window.addEventListener('crm:open-search', open);
    return () => window.removeEventListener('crm:open-search', open);
  }, []);

  const cycleMode = () => setMode(mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light');
  const modeIcon = mode === 'light' ? <LightModeIcon /> : mode === 'dark' ? <DarkModeIcon /> : <SettingsBrightnessIcon />;

  const openAvatar = (e: MouseEvent<HTMLElement>) => setAvatarAnchor(e.currentTarget);
  const signOut = () => {
    setAvatarAnchor(null);
    if (!DEV_AUTH_BYPASS) void instance.logoutRedirect();
  };

  const active = currentLanguage();

  const navLinks = NAV.map((item) => (
    <Button
      key={item.to}
      component={NavLink}
      to={item.to}
      end={item.end}
      onClick={() => setDrawerOpen(false)}
      data-testid={`nav-${item.testId}`}
      sx={{
        color: 'text.primary',
        px: 1.5,
        borderRadius: 0,
        borderBottom: '3px solid transparent',
        '&.active': { borderBottomColor: 'primary.main', color: 'primary.main' },
      }}
    >
      {t(item.labelKey)}
    </Button>
  ));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ minHeight: 56, gap: 1 }}>
          {isMobile && (
            <IconButton edge="start" onClick={() => setDrawerOpen(true)} aria-label={t('app.openNavigation')}>
              <MenuIcon />
            </IconButton>
          )}

          <Box
            component="img"
            src={resolved === 'dark' ? '/brand/logo-dark-bg.png' : '/brand/logo-light-bg.png'}
            alt={t('app.logoAlt')}
            height={34}
            sx={{ width: 'auto', cursor: 'pointer', mr: 2 }}
            onClick={() => navigate('/')}
          />

          {!isMobile && <Stack direction="row" spacing={0.5} sx={{ flexGrow: 1 }}>{navLinks}</Stack>}
          <Box sx={{ flexGrow: isMobile ? 1 : 0 }} />

          <Tooltip title={t('app.searchTooltip')}>
            <IconButton onClick={() => setSearchOpen(true)} aria-label={t('app.search')} data-testid="open-search">
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('app.language')}>
            <IconButton
              onClick={(e) => setLangAnchor(e.currentTarget)}
              aria-label={t('app.language')}
              data-testid="lang-toggle"
            >
              <TranslateIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('app.themeTooltip', { mode: t(`app.themeMode.${mode}`) })}>
            <IconButton onClick={cycleMode} aria-label={t('app.toggleTheme')} data-testid="theme-toggle">
              {modeIcon}
            </IconButton>
          </Tooltip>

          <Tooltip title={user?.name ?? t('app.account')}>
            <IconButton onClick={openAvatar} aria-label={t('app.account')} data-testid="avatar-button">
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13, fontWeight: 600 }}>
                {user?.initials ?? '?'}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 240, pt: 2 }}>
          <Stack sx={{ px: 1 }}>{navLinks}</Stack>
        </Box>
      </Drawer>

      <Menu anchorEl={langAnchor} open={Boolean(langAnchor)} onClose={() => setLangAnchor(null)}>
        {LANGUAGES.map((language) => (
          <MenuItem
            key={language.code}
            selected={language.code === active}
            onClick={() => {
              setLanguage(language.code);
              setLangAnchor(null);
            }}
            data-testid={`lang-option-${language.code}`}
          >
            <ListItemIcon>
              {language.code === active ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{language.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={avatarAnchor} open={Boolean(avatarAnchor)} onClose={() => setAvatarAnchor(null)}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="h4">{user?.name}</Typography>
          <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
        </Box>
        <Divider />
        <MenuItem onClick={signOut} disabled={DEV_AUTH_BYPASS}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{DEV_AUTH_BYPASS ? t('app.signOutDisabled') : t('app.signOut')}</ListItemText>
        </MenuItem>
      </Menu>

      <Box component="main" sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        <Outlet />
      </Box>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </Box>
  );
}
